import { WithSkeleton } from '@capsicum/common';
import { SkeletonItem, Text, Tooltip } from '@capsicum/common-web';
import { InfoIcon } from '@capsicum/common-web/icons';
import {
	useWCPledgeBasketTexts,
	useWCPledgeBonusPercentage
} from '@capsicum/features/world-championship-pledge/shared';
import React from 'react';
import styled from 'styled-components';

type WCPledgeBasketTotalPledgeCardProps = {
	formattedEstimatedCHZBonus: string;
	formattedTotalPledgeAmount: string;
	formattedTotalPledgeAmountUSDWithDoubleTilde: string;
	isEstimatedBonusLoading?: boolean;
};

const EstimatedCHZBonusTooltip = () => {
	const { intlTexts } = useWCPledgeBasketTexts();
	const { formattedBonusPercentage } = useWCPledgeBonusPercentage();

	return (
		<Tooltip>
			<Tooltip.Trigger testID="wcPledge-basketDetails-estimatedCHZBonus-tooltip">
				<S.BrandInfoIcon />
			</Tooltip.Trigger>
			<Tooltip.Content>
				<Tooltip.Description>
					<Text
						testID="wcPledge-basketDetails-estimatedCHZBonus-tooltip-text-with-variable-percentage"
						variant="text3"
					>
						{intlTexts.estimatedCHZBonusTooltipFirstText({ bonusPercentage: formattedBonusPercentage })}
					</Text>
					<br />
					<Text variant="text1">{intlTexts.estimatedCHZBonusTooltipSecondText}</Text>
				</Tooltip.Description>
			</Tooltip.Content>
		</Tooltip>
	);
};

const WCPledgeBasketTotalPledgeCardSkeleton = () => {
	const { intlTexts } = useWCPledgeBasketTexts();

	return (
		<S.TotalPledgeCard>
			<S.TotalPledgeCardRow>
				<Text variant="text1">Total Pledge</Text>
				<SkeletonItem height={50} width={120} />
			</S.TotalPledgeCardRow>
			<S.TotalPledgeCardRow>
				<S.TextWithTooltipContainer>
					<Text variant="text3">{intlTexts.estimatedCHZBonus}</Text>
					<EstimatedCHZBonusTooltip />
				</S.TextWithTooltipContainer>
				<SkeletonItem height={20} width={120} />
			</S.TotalPledgeCardRow>
		</S.TotalPledgeCard>
	);
};

const WCPledgeBasketTotalPledgeCardComponent = ({
	formattedEstimatedCHZBonus,
	formattedTotalPledgeAmount,
	formattedTotalPledgeAmountUSDWithDoubleTilde,
	isEstimatedBonusLoading
}: WCPledgeBasketTotalPledgeCardProps) => {
	const { intlTexts } = useWCPledgeBasketTexts();

	return (
		<S.TotalPledgeCard>
			<S.TotalPledgeCardRow>
				<Text variant="text1">{intlTexts.totalPledge}</Text>
				<S.TotalPledgeCardAmountContainer>
					<Text testID="wcPledge-basketDetails-totalPledgeAmount" variant="title5">
						{formattedTotalPledgeAmount}
					</Text>
					<S.TotalPledgeCardAmountCaption
						testID="wcPledge-basketDetails-totalPledgeAmountUSD"
						variant="text3"
					>
						{formattedTotalPledgeAmountUSDWithDoubleTilde}
					</S.TotalPledgeCardAmountCaption>
				</S.TotalPledgeCardAmountContainer>
			</S.TotalPledgeCardRow>
			<S.TotalPledgeCardRow>
				<S.TextWithTooltipContainer>
					<Text variant="text3">{intlTexts.estimatedCHZBonus}</Text>
					<EstimatedCHZBonusTooltip />
				</S.TextWithTooltipContainer>
				{isEstimatedBonusLoading ? (
					<SkeletonItem height={20} width={120} />
				) : (
					<Text testID="wcPledge-basketDetails-estimatedCHZBonus" variant="text1">
						{formattedEstimatedCHZBonus}
					</Text>
				)}
			</S.TotalPledgeCardRow>
		</S.TotalPledgeCard>
	);
};

export const WCPledgeBasketTotalPledgeCard = WithSkeleton(
	WCPledgeBasketTotalPledgeCardComponent,
	WCPledgeBasketTotalPledgeCardSkeleton
);

namespace S {
	export const TotalPledgeCard = styled.div`
		display: flex;
		flex-direction: column;
		padding: ${({ theme }) => theme.spacings.small};
		row-gap: ${({ theme }) => theme.spacings.xsmall};
		background-color: ${({ theme }) => theme.colors.backgrounds.main.subtle};
		border-radius: ${({ theme }) => theme.border.radius.medium['border-radius']};
	`;

	export const TotalPledgeCardRow = styled.div`
		display: flex;
		justify-content: space-between;
	`;

	export const TotalPledgeCardAmountContainer = styled.div`
		display: flex;
		flex-flow: column;
		text-align: right;
	`;

	export const TotalPledgeCardAmountCaption = styled(Text)`
		color: ${({ theme }) => theme.colors.text.main.subtle};
	`;

	export const BrandInfoIcon = styled(InfoIcon)`
		color: ${({ theme }) => theme.colors.icons.brand.enabled};
	`;

	export const TextWithTooltipContainer = styled.div`
		display: flex;
		align-items: center;
		column-gap: ${({ theme }) => theme.spacings.xxxsmall};
	`;
}
